import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { PhotoCard } from '@photos/shared';
import { useEscape } from '../hooks/useEscape';
import { thumbhashDataUrl } from '../lib/thumbhash';
import { Icon } from './Icon';

interface LightboxFrameProps {
  photos: PhotoCard[];
  /** size of the whole result set (more pages may load as you go) */
  total?: number;
  index: number;
  title: string;
  onIndex(i: number): void;
  onClose(): void;
  /** load more results when nearing the end */
  onNearEnd?(): void;
  /** a dialog is open on top: the arrow keys belong to it */
  paused?: boolean;
  /** the side panel */
  children: ReactNode;
}

/** Full-screen photo viewer: stage with previous/next, keyboard navigation and a side panel (Browse or Studio). */
export function LightboxFrame({ photos, total, index, title, onIndex, onClose, onNearEnd, paused, children }: LightboxFrameProps) {
  const p = photos[index];
  useEscape(onClose);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (index >= photos.length - 3) onNearEnd?.();
  }, [index, photos.length, onNearEnd]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || paused) return;
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      if (e.key === 'ArrowRight' && index < photos.length - 1) onIndex(index + 1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [index, photos.length, onIndex, paused]);

  useEffect(() => {
    if (!p) onClose();
  }, [p, onClose]);
  if (!p) return null;

  const ar = p.width / p.height;
  const ph = thumbhashDataUrl(p.thumbhash);

  return createPortal(
    <div className="lb open" role="dialog" aria-modal="true" aria-label={title}>
      <div className="stage">
        <button type="button" className="x" onClick={onClose}>
          <Icon name="x" /> Close
        </button>
        <span className="pos">
          {index + 1} / {Math.max(total ?? 0, photos.length)}
        </span>
        <button type="button" className="nav prev" aria-label="Previous" disabled={index === 0} onClick={() => onIndex(Math.max(0, index - 1))}>
          <Icon name="left" />
        </button>
        <img
          key={p.id}
          alt={title}
          src={p.previewUrl}
          style={{
            aspectRatio: String(ar),
            width: `min(100%, calc((100vh - 112px) * ${ar}))`,
            ...(ph ? { backgroundImage: `url(${ph})`, backgroundSize: 'cover' } : {}),
          }}
        />
        <button
          type="button"
          className="nav next"
          aria-label="Next"
          disabled={index >= photos.length - 1}
          onClick={() => onIndex(Math.min(photos.length - 1, index + 1))}
        >
          <Icon name="right" />
        </button>
      </div>
      <aside>{children}</aside>
    </div>,
    document.body,
  );
}
